import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";

import { cn } from "@/lib/utils";

/**
 * Label Component
 *
 * An accessible label component built on Radix UI Label primitive.
 * Provides proper accessibility for form controls with automatic associations.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Label htmlFor="email">Email Address</Label>
 * <Input id="email" type="email" />
 *
 * // With required indicator
 * <Label htmlFor="password" required>Password</Label>
 * <Input id="password" type="password" />
 *
 * // With optional indicator
 * <Label htmlFor="phone" optional>Phone Number</Label>
 * <Input id="phone" type="tel" />
 * ```
 */
export interface LabelProps extends React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> {
    /**
     * Shows a required indicator (*)
     */
    required?: boolean;
    /**
     * Shows an optional indicator (optional)
     */
    optional?: boolean;
}

const Label = React.forwardRef<React.ElementRef<typeof LabelPrimitive.Root>, LabelProps>(
    ({ className, children, required, optional, ...props }, ref) => (
        <LabelPrimitive.Root
            ref={ref}
            data-slot="label"
            className={cn(
                "setu:flex setu:items-center setu:gap-2 setu:text-sm setu:leading-none setu:font-medium setu:select-none setu:group-data-[disabled=true]:pointer-events-none setu:group-data-[disabled=true]:opacity-50 setu:peer-disabled:cursor-not-allowed setu:peer-disabled:opacity-50",
                className
            )}
            {...props}
        >
            {children}
            {required && (
                <span className="setu:ml-1 setu:text-destructive" aria-label="required">
                    *
                </span>
            )}
            {optional && !required && (
                <span className="setu:ml-1 setu:text-muted-foreground setu:text-xs">(optional)</span>
            )}
        </LabelPrimitive.Root>
    )
);
Label.displayName = LabelPrimitive.Root.displayName;

export { Label };
